import dayjs from "dayjs";
import { cloneDeep, intersection, isEqual, omitBy } from "lodash";
import { useConfirm } from "material-ui-confirm";

import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { createSearchParams, useNavigate } from "react-router-dom";

import NotificationsIcon from "@mui/icons-material/Notifications";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  LinearProgress,
  Link,
  Modal,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import {
  deleteAlertAction,
  getAlertAction,
  modifyAlertAction,
  toggleAlertModeAction,
  toggleAlertStatusAction,
  toggleAlertViewAction,
} from "@/api/alert";
import AlertModal from "@/components/AlertModal";
import AlertTab from "@/components/AlertTab";
import HorizontalScrollBar from "@/components/HorizontalScrollBar";
import InfiniteModeProInfo from "@/components/InfiniteModeProInfo";
import IntroAlert from "@/components/IntroAlert";
import { AlertTypeEnum, FlightWayEnum } from "@/constants";
import { AIRLINE_NAME_MAP } from "@/constants/airline";
import { siteName } from "@/constants/config";
import { FlightAlertSlogan, HotelAlertSlogan, PanoramaAlertSlogan } from "@/constants/config";
import { BANK_NAME_MAP, PROGRAM_NAME_MAP } from "@/constants/programs";
import { useAlertParamsContext } from "@/context/AlertParamsContext";
import useEvent from "@/hooks/useEvent";
import useNavigateLogin from "@/hooks/useNavigateLogin";
import usePlatform from "@/hooks/usePlatform";
import { useQuery } from "@/hooks/useQuery";
import { SearchConditionType } from "@/hooks/useSearchCondition";
import useUserInfo from "@/hooks/useUserInfo";
import { createSearchQuery } from "@/utils/searchQuery";

import AlertItem from "./AlertItem";
import AlertItemHotel from "./AlertItemHotel";
import LabelForm from "./LabelForm";
import AlertListSkeleton from "./Skeleton";
import Sort from "./Sort";
import { getSortFn } from "./Sort/sortConfig";
import { IAlertItem, formatResponse, sortFunction } from "./utils";

const MyAlert = () => {
  const { isPc, isMobile } = usePlatform();
  const query = useQuery<{ alertType: AlertTypeEnum }>();
  const { userInfoContext } = useUserInfo();
  const [loading, setLoading] = useState(true);
  const [changeStatusLoading, setChangeStatusLoading] = useState(false);
  const modalRef = useRef<typeof AlertModal>();
  const [alertList, setAlertList] = useState<IAlertItem[]>([]);
  const [usedInfo, setUsedInfo] = useState<{ alertUsed: number; alertLimit: number }>();

  const [currentActionAlertId, setCurrentActionAlertId] = useState<number>();
  const [currentActionAlert, setCurrentActionAlert] = useState<IAlertItem>();
  const [alertType, setAlertType] = useState<AlertTypeEnum>(
    query.alertType || AlertTypeEnum.Flight
  );

  const [infiniteModalVisible, setInfiniteModalVisible] = useState(false);
  const [labelModalVisible, setLabelModalVisible] = useState(false);

  const [selectLabels, setSelectLabels] = useState<string[]>([]);

  const [sortKey, setSortKey] = useState(0);

  const navigate = useNavigate();

  const { navigateLogin } = useNavigateLogin();

  const { setAlertParams, setRoutesAlertParams } = useAlertParamsContext();

  const confirm = useConfirm();

  const AlertItemComponent = useMemo(
    () => (alertType === AlertTypeEnum.Hotel ? AlertItemHotel : AlertItem),
    [alertType]
  );

  const filterAlertList = useMemo(() => {
    const _data = alertList.filter((item) => {
      return item.alert_type === alertType;
    });
    return getSortFn(alertType)(_data, sortKey);
  }, [alertList, alertType, sortKey]);

  const getAlertList = useEvent(async () => {
    try {
      setLoading(true);
      const res = await getAlertAction();
      setUsedInfo({
        alertUsed: res.alert_set,
        alertLimit: res.max_alert,
      });
      setAlertList(formatResponse(res.items).sort(sortFunction));
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  });

  const labelOptions = useMemo(() => {
    const list = filterAlertList
      .map((item) => {
        return item.filterData.labelNames;
      })
      .flat()
      .filter(Boolean);

    return Array.from(new Set(list));
  }, [filterAlertList]);

  const filterAlertListByLabel = useMemo(() => {
    if (!selectLabels.length) {
      return filterAlertList;
    }
    return filterAlertList.filter((item) => {
      return intersection(item.filterData?.labelNames ?? [], selectLabels).length > 0;
    });
  }, [filterAlertList, selectLabels]);

  useEffect(() => {
    getAlertList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeStatus = useEvent(async ({ status, alert_id }) => {
    try {
      setChangeStatusLoading(true);

      setCurrentActionAlertId(alert_id);
      await toggleAlertStatusAction({
        status: status,
        alert_id,
      });
      setAlertList((state) => {
        return state.map((item) => {
          if (item.alert_id === alert_id) {
            return {
              ...item,
              valid: status,
            };
          }
          return item;
        });
      });

      getAlertList();
      setChangeStatusLoading(false);
    } catch (error) {
      setChangeStatusLoading(false);
    }
  });

  const handleDelete = useEvent(async (alert_id) => {
    confirm({
      title: "Delete alert ?",
      dialogProps: {
        maxWidth: "xs",
      },
    })
      .then(async () => {
        await deleteAlertAction({ alert_id });
        getAlertList();
      })
      .catch((err) => {
        console.error(err);
      });
  });

  const handleEdit = useEvent((value: IAlertItem) => {
    const { filterData, info } = value;

    console.log("%c [  ]-192", "font-size:13px; background:pink; color:#bf2c9f;", value);

    let data = {};

    if (value.alert_type === AlertTypeEnum.Panorama) {
      data = {
        alert_type: value.alert_type,
        alertId: value.alert_id,
        passengers: Number(info.passengers),
        cabins: filterData.cabins.join("&"),
        fromLocation: info.departure || [],
        toLocation: info.arrival || [],
        departureDate: dayjs(info.departureDate).valueOf(),
        departureDateSec: dayjs(info.departureDateSec).valueOf(),
        stops: filterData.stops,
        banks: (filterData.banks || []).map((ele: any) => ({
          value: ele === "All" ? "" : ele,
          label: ele === "All" ? "All" : BANK_NAME_MAP[ele],
        })),
        airlines: (filterData.airline_programs || []).map((ele: any) => ({
          value: ele === "All" ? "" : ele,
          label: ele === "All" ? "All" : PROGRAM_NAME_MAP.get(ele),
        })),
        maxPoints: String(filterData.max_points || ""),
        mixed_cabin: String(filterData.mixed_cabin) ? filterData.mixed_cabin : null,
        flightNumber: filterData.flight_number || "",
        maxTax: filterData.tax || "",
        departureTime: filterData.departure_time ? filterData.departure_time : null,
        arrivalTime: filterData.arrival_time ? filterData.arrival_time : null,
        infiniteMode: value.infinite_mode,
        maxDuration: filterData.max_duration,
        valid: value.valid,

        areaCode: value.phone_number?.area ?? "",
        phoneNumber: value.phone_number?.number ?? "",
        agreeReceiveSMS: value.phone_number?.agreeReceiveSMS,
      };
    } else if (value.alert_type === AlertTypeEnum.Hotel) {
      data = {
        alert_type: value.alert_type,
        alertId: value.alert_id,
        fromLocation: info.departure || [],
        toLocation: info.arrival || [],
        departureDate: dayjs(info.departureDate).valueOf(),
        departureDateSec: dayjs(info.departureDateSec).valueOf(),
        maxPoints: String(filterData.max_miles || ""),
        infiniteMode: value.infinite_mode,
        valid: value.valid,
        hotelName: info.hotelName,
        areaCode: value.phone_number?.area ?? "",
        phoneNumber: value.phone_number?.number ?? "",
        agreeReceiveSMS: value.phone_number?.agreeReceiveSMS,
      };
    } else {
      data = {
        alert_type: value.alert_type,
        alertId: value.alert_id,
        passengers: Number(info.passengers),
        children: Number(info.children || 0),
        cabins: filterData.cabins.join("&"),
        fromLocation: (info.departure || []).map((ele) => ({ iata: ele })),
        toLocation: (info.arrival || []).map((ele) => ({ iata: ele })),
        departureDate: dayjs(info.departureDate).valueOf(),
        departureDateSec: dayjs(info.departureDateSec).valueOf(),
        stops: filterData.stops || "-1",
        banks: (filterData.banks || []).map((ele: any) => ({
          value: ele === "All" ? "" : ele,
          label: ele === "All" ? "All" : BANK_NAME_MAP[ele],
        })),
        airlines: (filterData.airline_programs || []).map((ele: any) => ({
          value: ele === "All" ? "" : ele,
          label: ele === "All" ? "All" : PROGRAM_NAME_MAP.get(ele),
        })),
        unwantedAirline: (filterData.unwanted_airline || []).map((ele: any) => ({
          value: ele,
          label: AIRLINE_NAME_MAP[ele],
        })),
        maxPoints: String(filterData.max_points || ""),
        mixed_cabin: String(filterData.mixed_cabin) ? filterData.mixed_cabin : null,
        flightNumber: filterData.flight_number || "",
        maxTax: filterData.tax || "",
        departureTime: filterData.departure_time ? filterData.departure_time : null,
        arrivalTime: filterData.arrival_time ? filterData.arrival_time : null,
        infiniteMode: value.infinite_mode,
        maxDuration: filterData.max_duration,
        valid: value.valid,
        areaCode: value.phone_number?.area ?? "",
        phoneNumber: value.phone_number?.number ?? "",
        agreeReceiveSMS: value.phone_number?.agreeReceiveSMS,
      };
    }
    modalRef.current && modalRef.current.show(value.alert_type, data);
  });

  const handleFlightView = useEvent(async (item: IAlertItem) => {
    const info = item.info;

    const condition = {
      flightWay: FlightWayEnum.OneWay,
      pax: info.passengers,
      children: info.children,
      cabins: item.filterData.cabins.join("&"),
      range: true,
      fromLocation: info.departure.map((v) => ({ iata: v })),
      toLocation: info.arrival.map((v) => ({ iata: v })),
      oneWayRangeStartDate: dayjs(info.departureDate).valueOf(),
      oneWayRangeEndDate: dayjs(info.departureDateSec).valueOf(),
    } as unknown as SearchConditionType;

    const filterParams = omitBy(
      {
        maxPoints: +item.filterData.max_points,
        stops: item.filterData.stops,
        banks: item.filterData.banks.filter((v: string) => v !== "All"),
        airlines: item.filterData.airline_programs
          .filter((v: string) => v !== "All")
          .map((code: string) => AIRLINE_NAME_MAP[code]),
        departureTime: item.filterData.departure_time,
        tax: +item.filterData.tax,
        premium: [item.filterData.mixed_cabin, 100],
        // flight_number: item.filterData.flight_number
      },
      (v, key) => {
        if (!v || v.length === 0) {
          return true;
        }

        if (key === "departureTime") {
          return isEqual(v, [0, 24]);
        }

        return false;
      }
    );

    console.log(filterParams);

    setAlertParams(filterParams);

    const params = createSearchQuery(condition);
    const search = createSearchParams(params).toString();

    navigate({
      pathname: "/flight",
      search: search,
    });

    await toggleAlertViewAction({ alert_id: item.alert_id });
    getAlertList();
  });

  const handlePanoramaView = useEvent(async (item: IAlertItem) => {
    const info = item.info;

    const condition = {
      from: info.departure.join("-"),
      to: info.arrival.join("-"),
      startDate: dayjs(info.departureDate).unix() + "",
      endDate: dayjs(info.departureDateSec).unix() + "",
    };

    const filterParams = omitBy(
      {
        banks: item.filterData.banks
          .filter((v: string) => v !== "All")
          .map((v: string) => BANK_NAME_MAP[v]),
        programs: item.filterData.airline_programs.filter((v: string) => v !== "All"),
      },
      (v) => {
        if (!v || v.length === 0) {
          return true;
        }

        return false;
      }
    );

    const search = createSearchParams({ ...condition }).toString();

    setRoutesAlertParams(filterParams);

    navigate({
      pathname: "/routes",
      search: search,
    });

    await toggleAlertViewAction({ alert_id: item.alert_id });
    getAlertList();
  });

  const handleChange = useEvent((checked, value) => {
    if (checked) {
      setSelectLabels([...selectLabels, value]);
    } else {
      setSelectLabels(selectLabels.filter((key) => key !== value));
    }
  });

  const renderLabelFilter = () => {
    return (
      <HorizontalScrollBar>
        <FormGroup className="w-[100%] flex items-center !flex-nowrap" row>
          {labelOptions.map((item) => (
            <FormControlLabel
              key={item}
              control={
                <Checkbox
                  checked={selectLabels.includes(item)}
                  onChange={(e) => handleChange(e.target.checked, item)}
                  name={item}
                />
              }
              label={item}
            />
          ))}
        </FormGroup>
      </HorizontalScrollBar>
    );
  };

  return (
    <div className="max-w-5xl mx-auto md:mt-[20px]">
      <Helmet>
        <html lang="en" />
        <title>AwardTool | AwardTool Award Alerts </title>
        <meta
          name="description"
          content="Get updates when the your ideal flight and hotel are available."
        />
      </Helmet>
      <Paper className="bg-[#fff] p-[20px] min-h-[90vh]">
        <div className="flex justify-between mb-[10px]">
          <Typography fontWeight={500}>{siteName + " Alerts"}</Typography>
          {
            <Button variant="outlined" startIcon={<NotificationsIcon />} size={"small"}>
              {usedInfo?.alertUsed} of {usedInfo?.alertLimit} Used
            </Button>
          }
        </div>

        <div className="mt-[13px] mb-[13px]">
          <IntroAlert />
        </div>
        <Button
          className="w-[100%] md:w-[auto] rounded-[38px]"
          variant="contained"
          onClick={() => {
            modalRef.current && modalRef.current.show(alertType, {});
          }}
          sx={{ borderRadius: '8px' }}
        >
          + Create Your Award Alert
        </Button>
        <div className="mt-[15px] flex justify-center">
          <AlertTab
            value={alertType}
            onChange={(v) => {
              setAlertType(v);
              navigate(`/alert?alertType=${v}`);
              setSelectLabels([]);
            }}
          />
        </div>
        <div className="flex justify-center mt-[10px] mb-[5px]">
          {alertType === AlertTypeEnum.Hotel ? (
            <Typography>{HotelAlertSlogan}</Typography>
          ) : alertType === AlertTypeEnum.Panorama ? (
            <Typography>{PanoramaAlertSlogan}</Typography>
          ) : (
            <Typography>{FlightAlertSlogan}</Typography>
          )}
        </div>
        {loading && (
          <div className="h-[25px] flex items-center">
            {loading && <LinearProgress className="w-[100%]" />}
          </div>
        )}

        {filterAlertList.length === 0 && !loading ? (
          <div className="mt-[80px] text-center">
            <Typography color="textSecondary">You haven't set any alert</Typography>
            <Typography color="textSecondary">Set one for your next wonderful trip!</Typography>
            {!userInfoContext.userInfo ? (
              <Typography color="textSecondary">
                A{" "}
                <Typography color="primary" className="inline">
                  free
                </Typography>{" "}
                account is needed for your award alert.
                <Typography>
                  <Typography color="textSecondary">
                    <Typography color="primary" className="inline">
                      <Typography
                        component={Link}
                        color="primary"
                        className="inline"
                        onClick={() => navigateLogin("/signup")}
                      >
                        Sign up
                      </Typography>{" "}
                      <Typography color="textSecondary" className="inline">
                        or
                      </Typography>{" "}
                      <Typography
                        component={Link}
                        href={"/login"}
                        color="primary"
                        className="inline"
                      >
                        Log in
                      </Typography>
                    </Typography>{" "}
                    <Typography className="inline">to enjoy 3 free award alerts.</Typography>
                  </Typography>
                </Typography>
              </Typography>
            ) : null}
          </div>
        ) : (
          <>
            <div className={isPc ? "flex justify-between mt-[10px]" : "flex justify-end mt-[10px]"}>
              {isPc ? <div className="border border-solid border-gray-300 p-2 rounded">
                <Typography color="primary">Each date/route counts as one alert.</Typography>
              </div> : null}
              <Sort type={alertType} value={sortKey} onChange={setSortKey} />
            </div>
            {renderLabelFilter()}
            <div className="space-y-[35px] mt-[10px]">
              {filterAlertListByLabel.map((item) => (
                <AlertItemComponent
                  key={item.alert_id}
                  data={item}
                  changeStatusLoading={
                    currentActionAlertId === item.alert_id ? changeStatusLoading : false
                  }
                  onEdit={() => {
                    handleEdit(item);
                  }}
                  onDelete={() => handleDelete(item.alert_id)}
                  onChangeStatus={(status) =>
                    changeStatus({
                      status,
                      alert_id: item.alert_id,
                    })
                  }
                  onInfiniteModeChange={async (status) => {
                    await toggleAlertModeAction({ alert_id: item.alert_id, status: status });

                    setAlertList((pre) =>
                      pre.map((ele) =>
                        ele.alert_id === item.alert_id ? { ...ele, infinite_mode: status } : ele
                      )
                    );
                  }}
                  onDetail={async () => {
                    if (alertType === AlertTypeEnum.Hotel) {
                      navigate({
                        pathname: "/hotel/details",
                        search: "?id=" + item.info.departure[0],
                      });
                    } else {
                      navigate({
                        pathname: "/alert-detail",
                        search: "?alert_id=" + item.alert_id,
                      });
                    }
                  }}
                  onView={async () => {
                    if (item.alert_type === AlertTypeEnum.Panorama) {
                      handlePanoramaView(item);
                    } else {
                      handleFlightView(item);
                    }
                  }}
                  onShowTips={() => setInfiniteModalVisible(true)}
                  onAddLabel={() => {
                    setLabelModalVisible(true);
                    setCurrentActionAlert(item);
                  }}
                  onRemoveLabel={async (labelName) => {
                    const cloneList = cloneDeep(alertList);
                    cloneList.find((indexItem) => {
                      if (item.alert_id === indexItem.alert_id) {
                        indexItem.filterData.labelNames = item.filterData.labelNames.filter(
                          (v: string) => v !== labelName
                        );
                      }
                    });
                    setAlertList(cloneList);

                    await modifyAlertAction({
                      alert_id: String(item.alert_id),
                      filter_criteria: {
                        ...item.filterData,
                        labelNames: item.filterData.labelNames.filter((v) => v !== labelName),
                      },
                      info: item.info,
                    });
                  }}
                />
              ))}
            </div>
          </>
        )}

        {loading && filterAlertList.length === 0 ? <AlertListSkeleton /> : null}
      </Paper>
      {/* <Divider /> */}

      <AlertModal
        ref={modalRef}
        onSuccess={getAlertList}
        onShowTips={() => setInfiniteModalVisible(true)}
      />

      <Modal open={infiniteModalVisible} onClose={() => setInfiniteModalVisible(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            p: 4,
            boxShadow: 2,
            borderRadius: "12px",
          }}
        >
          <Typography color="textPrimary">{<InfiniteModeProInfo />}</Typography>
        </Box>
      </Modal>

      <LabelForm
        data={currentActionAlert}
        open={labelModalVisible}
        onClose={() => setLabelModalVisible(false)}
        onSuccess={getAlertList}
      />
    </div>
  );
};

export default MyAlert;
